import React, { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, MagnifyingGlass } from "@phosphor-icons/react";
import { exportToCSV } from "@/lib/csvExport";

function exportCSV(rows, cols, filename) {
  exportToCSV(rows, cols, filename);
}

export function StockReport() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/reports/stock").then(r => setRows(r.data)); }, []);
  const filtered = rows.filter(r => !q || `${r.item_code} ${r.item_name} ${r.brand}`.toLowerCase().includes(q.toLowerCase()));
  const cols = [
    { key: "item_code", label: "Item Code" },
    { key: "item_name", label: "Item Name" },
    { key: "brand", label: "Brand" },
    { key: "category", label: "Category" },
    { key: "current_stock", label: "Stock" },
    { key: "purchase_price", label: "Purchase Price", get: r => r.purchase_price },
    { key: "sale_price", label: "Sale Price", get: r => r.sale_price },
    { key: "stock_value", label: "Stock Value", get: r => (r.current_stock * r.purchase_price).toFixed(2) },
  ];
  return (
    <div className="space-y-4" data-testid="stock-report-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold">Stock Report</h1>
          <p className="text-sm text-muted-foreground mt-1">All items with current stock and valuation.</p>
        </div>
        <Button onClick={() => exportCSV(filtered, cols, "stock_report.csv")} data-testid="stock-report-export" variant="outline">
          <Download size={16} className="mr-1" /> Export CSV
        </Button>
      </div>
      <Card className="p-0 border border-border overflow-hidden">
        <div className="p-4 border-b border-border flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search..." value={q} onChange={e => setQ(e.target.value)} className="pl-9" />
          </div>
          <div className="text-xs text-muted-foreground">{filtered.length} items</div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>{cols.map(c => <TableHead key={c.key} className={["stock_value","purchase_price","sale_price","current_stock"].includes(c.key)?"text-right":""}>{c.label}</TableHead>)}</TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(r => (
              <TableRow key={r.id}>
                <TableCell className="font-mono text-xs">{r.item_code}</TableCell>
                <TableCell>{r.item_name}</TableCell>
                <TableCell>{r.brand}</TableCell>
                <TableCell>{r.category}</TableCell>
                <TableCell className="text-right tabular-nums font-semibold">{r.current_stock}</TableCell>
                <TableCell className="text-right tabular-nums">{money(r.purchase_price)}</TableCell>
                <TableCell className="text-right tabular-nums">{money(r.sale_price)}</TableCell>
                <TableCell className="text-right tabular-nums font-semibold">{money(r.current_stock * r.purchase_price)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

export function LowStock() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/reports/low-stock").then(r => setRows(r.data)); }, []);
  const cols = [
    { label: "Item Code", key: "item_code" },
    { label: "Item Name", key: "item_name" },
    { label: "Brand", key: "brand" },
    { label: "Current Stock", key: "current_stock" },
    { label: "Reorder Level", key: "reorder_level" },
    { label: "Shortfall", key: "shortfall", get: r => Math.max(0, r.reorder_level - r.current_stock) },
  ];
  return (
    <div className="space-y-4" data-testid="low-stock-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold">Low Stock Alert</h1>
          <p className="text-sm text-muted-foreground mt-1">Items at or below reorder level — reorder soon.</p>
        </div>
        <Button variant="outline" onClick={() => exportCSV(rows, cols, "low_stock")} data-testid="low-stock-export">
          <Download size={16} className="mr-1" /> Export CSV
        </Button>
      </div>
      <Card className="p-0 border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Item Code</TableHead>
              <TableHead>Item Name</TableHead>
              <TableHead>Brand</TableHead>
              <TableHead className="text-right">Current Stock</TableHead>
              <TableHead className="text-right">Reorder Level</TableHead>
              <TableHead className="text-right">Shortfall</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.id}>
                <TableCell className="font-mono text-xs">{r.item_code}</TableCell>
                <TableCell>{r.item_name}</TableCell>
                <TableCell>{r.brand}</TableCell>
                <TableCell className="text-right tabular-nums text-destructive font-semibold">{r.current_stock}</TableCell>
                <TableCell className="text-right tabular-nums">{r.reorder_level}</TableCell>
                <TableCell className="text-right tabular-nums font-semibold">{Math.max(0, r.reorder_level - r.current_stock)}</TableCell>
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">No low stock items. All good!</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

export function StockMovements() {
  const [rows, setRows] = useState([]);
  const [mtype, setMtype] = useState("");
  useEffect(() => {
    api.get(`/stock-movements${mtype ? `?mtype=${mtype}` : ""}`).then(r => setRows(r.data));
  }, [mtype]);
  const cols = [
    { label: "Date", key: "created_at", get: r => new Date(r.created_at).toLocaleString("en-IN") },
    { label: "Type", key: "movement_type" },
    { label: "Item Code", key: "item_code" },
    { label: "Item Name", key: "item_name" },
    { label: "Qty", key: "qty" },
    { label: "Reference", key: "reference_no" },
    { label: "Remarks", key: "remarks" },
  ];
  const badge = (t) => {
    const map = {
      PURCHASE: "bg-blue-100 text-blue-700",
      SALE: "bg-purple-100 text-purple-700",
      ISSUE: "bg-orange-100 text-orange-700",
      RETURN_FRESH: "bg-green-100 text-green-700",
      CONSUMED: "bg-gray-200 text-gray-700",
    };
    return map[t] || "bg-secondary text-muted-foreground";
  };
  return (
    <div className="space-y-4" data-testid="movements-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold">Stock Movements</h1>
          <p className="text-sm text-muted-foreground mt-1">Complete audit trail of stock changes.</p>
        </div>
        <Button variant="outline" onClick={() => exportCSV(rows, cols, "stock_movements")} data-testid="movements-export">
          <Download size={16} className="mr-1" /> Export CSV
        </Button>
      </div>
      <Card className="p-0 border border-border overflow-hidden">
        <div className="p-4 border-b border-border flex items-center gap-3">
          <select value={mtype} onChange={e => setMtype(e.target.value)} className="h-10 px-3 border border-border rounded-md bg-card text-sm" data-testid="movements-filter">
            <option value="">All Types</option>
            <option value="PURCHASE">Purchase</option>
            <option value="SALE">Sale</option>
            <option value="ISSUE">Issue to Tech</option>
            <option value="RETURN_FRESH">Return (Fresh)</option>
            <option value="CONSUMED">Consumed</option>
          </select>
          <div className="text-xs text-muted-foreground">{rows.length} movements</div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Item</TableHead>
              <TableHead className="text-right">Qty</TableHead>
              <TableHead>Reference</TableHead>
              <TableHead>Remarks</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.id}>
                <TableCell className="text-xs">{new Date(r.created_at).toLocaleString("en-IN")}</TableCell>
                <TableCell>
                  <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-semibold tracking-wider ${badge(r.movement_type)}`}>
                    {r.movement_type}
                  </span>
                </TableCell>
                <TableCell>{r.item_name || r.item_id}</TableCell>
                <TableCell className="text-right tabular-nums">{r.qty}</TableCell>
                <TableCell className="font-mono text-xs">{r.reference_no}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{r.remarks}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
